import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import type { SelectChangeEvent } from '@mui/material'
import { gridFilterModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import GridSearchOutlined from '@/components/dataDisplay/GridSearchOutlined'
import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import { FIELD_WIDTH } from '@/constants/layout'
import type { ActivationGroup } from '@/features/activationGroup/types'
import { ActivationGroupState, ActivationProvider } from '@/features/activationGroup/types'
import { getActivationProviderLabel } from '@/features/activationGroup/utils/activationGroups'
import type { MarketProgramBiddingName } from '@/features/bidding/types/bid'
import { getFilterItemValue, updateDataGridFilter } from '@/utils/datagrid/filters'
import { filterUnique } from '@/utils/filterUnique'
import { sortOptionsAlphabetically } from '@/utils/sortOptionsAlphabetically'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends ActivationGroupsDataGridToolbarProps {}
}

export type ActivationGroupsDataGridToolbarProps = {
  onAddNewActivationGroup: () => void
  activationGroups: ActivationGroup[] | null
}

const ActivationGroupsDataGridToolbar = ({
  onAddNewActivationGroup,
  activationGroups,
}: ActivationGroupsDataGridToolbarProps) => {
  const { t } = useTranslation()
  const apiRef = useGridApiContext()

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const marketProgram = getFilterItemValue<MarketProgramBiddingName>(filterModel, 'marketProgram') ?? ''
  const portfolio = getFilterItemValue<string>(filterModel, 'portfolioCode') ?? ''
  const state = getFilterItemValue<string>(filterModel, 'state') ?? ''
  const activationProvider = getFilterItemValue<string>(filterModel, 'activationProvider') ?? ''

  const handleFilterChange = (field: string, value: string) => {
    updateDataGridFilter(apiRef, field, { id: field, field, operator: 'equals', value })
  }

  const blankOption = { value: '', label: '-', id: `filter-item-blank` }

  return (
    <GridToolbar
      actions={
        <CustomIconButton
          Icon={AddOutlinedIcon}
          aria-label={t('activation_groups.create_activation_group_button_label')}
          color="primary"
          variant={'solid'}
          onClick={onAddNewActivationGroup}
        />
      }
      filters={
        <>
          <GridSearchOutlined
            label={t('common.navigation.search')}
            placeholder={t('activation_groups.table.header.search')}
          />

          <CustomSelectField
            data-testid="state-selector"
            id="state"
            label={t('activation_groups.table.header.state')}
            options={[blankOption, ...getStateOptions(t)]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={state}
            onChange={(event: SelectChangeEvent) => handleFilterChange('state', event.target.value)}
          />
          <CustomSelectField
            data-testid="portfolio-selector"
            id="portfolioCode"
            label={t('bidding.overview.header.portfolio')}
            options={[blankOption, ...getPortfolioCodeOptions(activationGroups)]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={portfolio}
            onChange={(event: SelectChangeEvent) => handleFilterChange('portfolioCode', event.target.value)}
          />
          <CustomSelectField
            data-testid="market-program-selector"
            id="marketProgram"
            label={t('bidding.overview.header.market_program')}
            options={[blankOption, ...getMarketProgramOptions(activationGroups, t)]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={marketProgram}
            onChange={(event: SelectChangeEvent) => handleFilterChange('marketProgram', event.target.value)}
          />
          <CustomSelectField
            data-testid="activation-provider-selector"
            id="activationProvider"
            label={t('activation_groups.activation_provider_label')}
            options={[blankOption, ...getActivationProviderOptions(t)]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={activationProvider}
            onChange={(event: SelectChangeEvent) => handleFilterChange('activationProvider', event.target.value)}
          />
        </>
      }
    />
  )
}

const getStateOptions = (t: TFunction<'translation'>): SelectFieldOption<string>[] =>
  Object.values(ActivationGroupState).map((value) => ({
    id: value,
    label: t(`activation_groups.state.${value}`),
    value: value,
  }))

const getPortfolioCodeOptions = (activationGroups: ActivationGroup[] | null): SelectFieldOption<string>[] => {
  const selectFields = (activationGroups || []).map((activationGroup) => ({
    id: activationGroup.portfolio.code,
    value: activationGroup.portfolio.code,
    label: activationGroup.portfolio.code,
  }))
  return sortOptionsAlphabetically(filterUnique(selectFields))
}

const getMarketProgramOptions = (activationGroups: ActivationGroup[] | null, t: TFunction<'translation'>) => {
  const selectFields = (activationGroups || []).map((activationGroup) => ({
    id: activationGroup.marketProgram,
    value: t(`bidding.market_program.${activationGroup.marketProgram}`),
    label: t(`bidding.market_program.${activationGroup.marketProgram}`),
  }))
  return sortOptionsAlphabetically(filterUnique(selectFields))
}

const getActivationProviderOptions = (t: TFunction<'translation'>): SelectFieldOption<string>[] =>
  sortOptionsAlphabetically(
    Object.values(ActivationProvider).map((provider) => ({
      id: provider,
      value: provider,
      label: getActivationProviderLabel(provider, t),
    })),
  )

export default ActivationGroupsDataGridToolbar
